import React, {useState} from 'react'
import View from './view'
import {useAppDispatch} from '../../../../redux/configureStore'
import {
  getStudyGuide,
  startStudyGuide,
  StudyGuide,
} from '../../../../redux/studyGuides'
import {toggleErrorDialog} from '../../../../redux/config'
import {useAuth} from '../../../../components/AuthProvider'
import {useNavigate} from 'react-router-dom'
import {Attempt, submitExamDate} from '../../../../redux/reportExamStatus'
import {
  getOrganizationCourses,
  updateOrgCourseState,
} from '../../../../redux/courses'
import {selectMember} from '../../../../redux/members'
import {useSelector} from 'react-redux'
import {handleUpgrade} from '../../../../utils/upgradeHelper'

type Props = {
  open: boolean
  setOpen: (value: boolean) => void
  reportStatus?: Attempt
  studyGuides?: StudyGuide[]
  studyGuide?: StudyGuide
  isCard?: boolean
}

export type StudyPlanStep = 'study-mode' | 'exam-status' | 'days' | 'migrate'
export type StudyPlanStudyMode = 'flexible' | 'guided' | null

const StudyPlanDialog = ({
  open,
  studyGuides,
  reportStatus,
  setOpen,
  studyGuide,
  isCard,
}: Props) => {
  const [dateFilter, setDateFilter] = useState('')
  const [step, setStep] = useState<StudyPlanStep>('study-mode')
  const [studyMode, setStudyMode] = useState<StudyPlanStudyMode>(null)
  const [days, setDays] = useState<number>(0)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const member = useSelector(selectMember)

  const auth = useAuth()
  const dispatch = useAppDispatch()

  const handleChangeStep = (value: StudyPlanStep) => {
    setStep(value)
  }

  const handleChangeStudyMode = (value: StudyPlanStudyMode) => {
    setStudyMode(value)
  }

  const handleChangeDays = (value: number) => {
    setDays(value)
  }

  const handleChangeDateFilter = (value: string) => {
    setDateFilter(value)
  }

  const handleClose = () => {
    setOpen(false)
    setStep('study-mode')
    setStudyMode(null)
    setDateFilter('')
  }

  const handleSubmitExamDate = async () => {
    if (studyGuides && dateFilter) {
      const courseId = studyGuides[0].courseId

      setIsSubmitting(true)

      const submitFields = {
        auth,
        courseId: courseId,
        examDate: new Date(dateFilter).toISOString().split('T')[0],
        pass: null,
      }

      try {
        const {type} = await dispatch(submitExamDate(submitFields))
        if (type === submitExamDate.rejected.type) {
          setIsSubmitting(false)
          throw new Error('Failed to submit exam schedule')
        }

        setIsSubmitting(false)
        handleChangeStep('days')
      } catch (e) {
        toggleErrorDialog({
          opened: true,
          error:
            'We were unable to submit or update your exam schedule, please retry. If issue persists please contact our support team',
        })
      }
    }
  }

  const navigate = useNavigate()

  const goToUrl = (url: string) => {
    navigate(url, {replace: true})
  }

  const handleSubmit = async (studyGuide?: StudyGuide, migrate?: boolean) => {
    if (studyGuide) {
      setIsSubmitting(true)
      try {
        const clear = migrate === false

        const {type} = await dispatch(
          startStudyGuide({
            auth,
            courseId: studyGuide.courseId,
            studyGuideId: studyGuide.id,
            migrate,
            clear,
          }),
        )
        if (type === startStudyGuide.rejected.type) {
          setIsSubmitting(false)
          throw new Error('Unable to start study guide')
        } else if (type === startStudyGuide.fulfilled.type) {
          setIsSubmitting(false)
          if (studyGuide.timeWindow > 0) {
            if (isCard) {
              await dispatch(getStudyGuide({auth, studyGuideId: studyGuide.id}))
            } else {
              goToUrl(`/study-guide/${studyGuide.id}`)
            }
          } else {
            goToUrl(`/profile/dashboard/${studyGuide.courseId}`)
          }

          await dispatch(
            updateOrgCourseState({
              courseId: studyGuide.courseId,
              studyGuideId: studyGuide.id,
            }),
          )

          await dispatch(getOrganizationCourses({auth, keepState: true}))
        }
      } catch (e) {
        await dispatch(
          toggleErrorDialog({
            opened: true,
            error:
              'We were unable to start the guided process, please retry. If issues persist contact our support team',
          }),
        )
      }
    }
  }

  return (
    <View
      open={open}
      onClose={handleClose}
      onChangeStep={handleChangeStep}
      step={step}
      onChangeStudyMode={handleChangeStudyMode}
      studyMode={studyMode}
      onChangeDateFilter={handleChangeDateFilter}
      dateFilter={dateFilter}
      onSubmitExamDate={handleSubmitExamDate}
      onChangeDays={handleChangeDays}
      days={days}
      studyGuides={studyGuides}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      reportStatus={reportStatus}
      studyGuide={studyGuide}
      onTrackUpgrade={handleUpgrade(
        dispatch,
        auth,
        member,
        'proven process paywall',
      )}
    />
  )
}

export default StudyPlanDialog
